<template>
  <div class="journal-container" v-if="journalList && journalList.length > 0">
    <!-- 折叠/展开按钮 -->
    <div class="journal-toggle-btn" @click="toggleJournalListCollapse" :title="isJournalListCollapsed ? '展开期刊列表' : '折叠期刊列表'">
      <span class="toggle-icon" :class="{ 'collapsed': isJournalListCollapsed }">
        {{ isJournalListCollapsed ? '▲' : '▼' }}
      </span>
      <span class="toggle-text">期刊列表 ({{ journalList.length }})</span>
    </div>
    
    <!-- 期刊列表 -->
    <div class="journal-list" :class="{ 'collapsed': isJournalListCollapsed }" @scroll="handleScroll">
      <div 
        v-for="(journal, index) in journalList" 
        :key="journal.thesis_journal_id"
        class="journal-item"
        :class="[
          getJournalStyleClass(index),
          { 'selected': selectedJournalId === journal.thesis_journal_id }
        ]"
        @click="handleJournalClick(journal)"
        :title="journal.thesis_journal_name"
      >
        {{ journal.thesis_journal_name }}
      </div>
      
      <!-- 加载更多提示 -->
      <div v-if="isLoading" class="loading-more">
        <span class="loading-text">加载中...</span>
      </div>
      
      <!-- 没有更多数据提示 -->
      <div v-if="!hasMore && journalList.length > 0" class="no-more">
        <span class="no-more-text">已加载全部期刊</span>
      </div>
    </div>
  </div>
</template>

<script>
import request from '@/http/request'

export default {
  name: 'JournalList',
  data() {
    return {
      journalList: [],
      isJournalListCollapsed: true,
      selectedJournalId: null,
      currentPage: 1,
      pageSize: 3000,
      isLoading: false,
      hasMore: true
    }
  },
  methods: {
    // 获取期刊列表
    async getJournalList(page = 1, append = false) {
      if (this.isLoading) return
      
      this.isLoading = true
      
      try {
        const response = await request('/api/Thesis/getThesisJournalList', { page }, 'GET')
        
        let newJournals = []
        if (response?.code === 200 && Array.isArray(response.result)) {
          newJournals = response.result
        } else if (Array.isArray(response)) {
          newJournals = response
        }
        
        if (append) {
          this.journalList = [...this.journalList, ...newJournals]
        } else {
          this.journalList = newJournals
        }
        
        this.hasMore = newJournals.length === this.pageSize
        
      } catch (error) {
        console.error('获取期刊列表失败:', error)
        if (!append) {
          this.journalList = []
        }
      } finally {
        this.isLoading = false
      }
    },

    // 加载更多期刊
    async loadMoreJournals() {
      if (!this.hasMore || this.isLoading) return
      
      this.currentPage += 1
      await this.getJournalList(this.currentPage, true)
    },

    // 处理滚动事件
    handleScroll(event) {
      const { scrollTop, scrollHeight, clientHeight } = event.target
      if (scrollHeight - scrollTop - clientHeight < 50) {
        this.loadMoreJournals()
      }
    },

    // 切换期刊列表的折叠状态
    toggleJournalListCollapse() {
      this.isJournalListCollapsed = !this.isJournalListCollapsed
    },

    // 处理期刊点击事件
    handleJournalClick(journal) {
      if (this.selectedJournalId === journal.thesis_journal_id) {
        this.selectedJournalId = null
        this.$emit('journal-search', null)
      } else {
        this.selectedJournalId = journal.thesis_journal_id
        this.$emit('journal-search', journal)
      }
    },

    // 获取期刊样式类名（10种样式循环）
    getJournalStyleClass(index) {
      return `journal-style-${(index % 10) + 1}`
    },

    // 重置选中状态
    resetSelection() {
      this.selectedJournalId = null
    },

    // 重置分页状态
    resetPagination() {
      this.currentPage = 1
      this.journalList = []
      this.hasMore = true
      this.isLoading = false
    }
  },
  mounted() {
    this.getJournalList()
  }
}
</script>

<style lang="scss" scoped>
/* 期刊展示样式 */
.journal-container {
  position: fixed;
  top: 10vh;
  right: 2vw;
  width: 28vw;
  max-height: 70vh;
  z-index: 5;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #dee2e6;
  overflow: hidden;
  transition: all 0.3s ease;
}

.journal-toggle-btn {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 12px;
  background: #e9ecef;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 1px solid #dee2e6;
  
  &:hover {
    background: #dee2e6;
  }
  
  .toggle-icon {
    color: #6c757d;
    font-size: 12px;
    transition: transform 0.2s ease;
    
    &.collapsed {
      transform: rotate(180deg);
    }
  }
  
  .toggle-text {
    color: #333;
    font-size: 11px;
    font-weight: 500;
  }
}

.journal-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 6px;
  max-height: 60vh;
  overflow-y: auto;
  transition: all 0.3s ease;
  
  &.collapsed {
    max-height: 0; /* 完全隐藏 */
    overflow: hidden;
    padding: 0; /* 折叠时移除内边距 */
  }
}

.journal-item {
  padding: 3px 8px;
  border-radius: 12px;
  color: #333;
  font-size: 10px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid #adb5bd;
  background-color: #ffffff;
  position: relative;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 200px;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(173, 181, 189, 0.3);
    border-color: #6c757d;
    background-color: #e9ecef;
  }
}

/* 期刊样式 */
.journal-style-1 { background-color: #e3f2fd; border-color: #007bff; }
.journal-style-2 { background-color: #e8f5e8; border-color: #28a745; }
.journal-style-3 { background-color: #fff3e0; border-color: #fd7e14; }
.journal-style-4 { background-color: #f3e5f5; border-color: #6f42c1; }
.journal-style-5 { background-color: #e0f2f1; border-color: #20c997; }
.journal-style-6 { background-color: #ffebee; border-color: #dc3545; }
.journal-style-7 { background-color: #fffde7; border-color: #ffc107; }
.journal-style-8 { background-color: #fce4ec; border-color: #e83e8c; }
.journal-style-9 { background-color: #e3f2fd; border-color: #0056b3; }
.journal-style-10 { background-color: #d4edda; border-color: #155724; }

/* 期刊状态样式 */
.journal-item:hover {
  background-color: #e9ecef !important;
  border-color: #adb5bd !important;
}

.journal-item.selected {
  background: #ff6b35 !important;
  color: #ffffff !important;
  border-color: #e55a2b !important;
  box-shadow: 0 0 8px rgba(255, 107, 53, 0.4) !important;
}

/* 加载状态样式 */
.loading-more, .no-more {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 8px;
  width: 100%;
}

.loading-text {
  color: #6c757d;
  font-size: 11px;
  animation: pulse 1.5s infinite;
}

.no-more-text {
  color: #adb5bd;
  font-size: 10px;
  font-style: italic;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* 滚动条样式 */
.journal-list::-webkit-scrollbar {
  width: 4px;
}

.journal-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 2px;
}

.journal-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.journal-list::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .journal-container {
    top: 12vh;
    right: 1vw;
    width: 35vw;
    max-height: 60vh;
  }
  
  .journal-list.collapsed {
    max-height: 0;
    padding: 0;
  }
  
  .journal-list {
    max-height: 50vh;
  }
  
  .journal-item {
    padding: 2px 6px;
    font-size: 9px;
    max-width: 150px;
  }
  
  .journal-toggle-btn {
    padding: 4px 8px;
  }
  
  .toggle-text {
    font-size: 10px;
  }
}
</style>
